<template>
  <el-card shadow="never" class="aui-card--fill" style="font-family: 'fontStyle';">
    <div class="mod-informationinquiry__chart">
      <!-- toolbar: axis pickers -->
      <div class="chart-toolbar">
        <div class="axis-pickers">
          <span class="axis-picker-label">X-Axis:</span>
          <el-select v-model="xMetricKey" size="small" class="axis-select" @change="renderChart">
            <el-option v-for="m in metricOptions" :key="'x-' + m.key" :label="m.label" :value="m.key" />
          </el-select>
          <span class="axis-picker-label axis-picker-label--gap">Y-Axis:</span>
          <el-select v-model="yMetricKey" size="small" class="axis-select" @change="renderChart">
            <el-option v-for="m in metricOptions" :key="'y-' + m.key" :label="m.label" :value="m.key" />
          </el-select>
        </div>
        <div class="updated-note" v-if="syncTime">UPDATED&nbsp;&nbsp;{{ syncTime }}</div>
      </div>

      <!-- chart area + collapsible portfolio panel -->
      <div class="chart-wrap">
        <div ref="chartEl" class="chart-canvas" :class="{ 'chart-canvas--shrunk': panelOpen }" v-loading="loading"></div>

        <div class="portfolio-panel" :class="{ 'portfolio-panel--open': panelOpen }">
          <div class="panel-toggle" @click="togglePanel">PORTFOLIO</div>
          <div class="panel-body">
            <div class="panel-header">
              <span class="panel-title">Portfolio Holdings</span>
              <span class="panel-status" :class="{ 'panel-status--error': saveStatus === 'Save failed' }">{{ saveStatus }}</span>
            </div>
            <div class="panel-subnote">
              Checked names are highlighted in gold on the chart. The selection is shared — the last saved change is what all users see.
            </div>
            <div class="panel-list">
              <el-checkbox-group v-model="selectedTickers" @change="selectionChangedHandle">
                <el-checkbox v-for="row in tickerList" :key="row.raw" :label="row.raw" class="panel-item">
                  {{ row.display }}
                </el-checkbox>
              </el-checkbox-group>
            </div>
            <div class="panel-footer">
              <span>{{ selectedTickers.length }} of {{ tickerList.length }} selected</span>
              <a class="panel-clear" @click="clearSelection">Clear all</a>
            </div>
          </div>
        </div>
      </div>

      <div class="metric-note" v-if="metricNote">{{ metricNote }}</div>
      <div class="excluded-note" v-if="excludedNote">{{ excludedNote }}</div>
    </div>
  </el-card>
</template>

<script>
import * as echarts from 'echarts';

// 'derived' metrics are computed from two stored fields rather than read straight off
// the row. DB field names (see ExcelUtils field map): bearIrr = BEAR IRR (K19),
// bearWeight = BEAR WEIGHT (L19), irr = IRR (G21), qsAdjIrr = QS ADJ IRR (I21),
// riskReward = R/R (F21), qs = QS (K21).
const METRICS = [
  { key: 'riskReward', label: 'R/R', kind: 'x' },
  { key: 'qs', label: 'QS', kind: 'num' },
  { key: 'irr', label: 'IRR', kind: 'pct' },
  { key: 'qsAdjIrr', label: 'QS-Adj. IRR', kind: 'pct' },
  { key: 'padjBear', label: 'Probability-Adj. Bear IRR', kind: 'pct', derived: true },
];

// golden yellow for portfolio holdings, light gray for the rest of the universe
const COLOR_PORTFOLIO = '#FFC000';
const COLOR_PORTFOLIO_BORDER = '#D89E00';
const COLOR_PORTFOLIO_LABEL = '#1F2329';
const COLOR_UNIVERSE = '#C4C9D2';
const COLOR_UNIVERSE_BORDER = '#A8AEB8';
const COLOR_UNIVERSE_LABEL = '#8A8F98';

export default {
  data() {
    return {
      loading: false,
      rows: [],
      selectedTickers: [],
      xMetricKey: 'padjBear',  // default: Probability-Adj. Bear IRR on X
      yMetricKey: 'irr',       // default: IRR on Y
      metricOptions: METRICS,
      panelOpen: false,
      saveStatus: '',
      syncTime: '',
      excludedNote: '',
      metricNote: '',
    };
  },
  computed: {
    tickerList() {
      return this.rows
        .map((r) => ({ raw: (r.ticker || '').trim(), display: this.displayTicker(r.ticker) }))
        .filter((r) => r.raw)
        .sort((a, b) => a.display.localeCompare(b.display));
    },
  },
  mounted() {
    this.chart = echarts.init(this.$refs.chartEl);
    this.resizeHandle = () => { this.refit(); };
    window.addEventListener('resize', this.resizeHandle);
    this.init();
  },
  activated() {
    // keep-alive re-entry: tab stays loaded for the whole session (no refetch),
    // just re-fit the canvas in case the layout changed while hidden
    this.$nextTick(() => { this.refit(); });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeHandle);
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
  methods: {
    init() {
      this.loading = true;
      Promise.all([
        this.$http.get('/sys/sysstock/page', {
          params: { page: 1, limit: 1000, orderFields: 'ticker', orderWay: 'asc' },
        }),
        this.$http.get('/sys/sysstock/portfolioSelection'),
        this.$http.get('/sys/sysstock/getSyncTime'),
      ])
        .then(([stockRes, selRes, timeRes]) => {
          this.rows = (stockRes.data.data && stockRes.data.data.list) || [];
          this.selectedTickers = selRes.data.data || [];
          this.syncTime = timeRes.data.data || '';
          this.loading = false;
          this.renderChart();
        })
        .catch(() => {
          this.loading = false;
        });
    },
    // "TSM US" -> "TSM"; non-US tickers keep the exchange code ("SAF FP", "700 HK")
    displayTicker(t) {
      return String(t || '').trim().replace(/\s+US$/i, '');
    },
    // "27%" / "-6%" / "1.0x" / "12" / "2.47" -> number; ""/null/"-"/junk -> null
    parseMetric(s) {
      if (s == null) return null;
      s = String(s).trim();
      if (!s || s === '-') return null;
      var neg = false;
      if (s.charAt(0) === '(' && s.charAt(s.length - 1) === ')') {
        neg = true;
        s = s.slice(1, -1).trim();
      }
      s = s.replace(/[%x,\s]/gi, '');
      var v = parseFloat(s);
      if (isNaN(v)) return null;
      return neg ? -v : v;
    },
    // raw stored string -> display string matching the Table's negative convention: -6% -> (6%)
    displayValue(s) {
      if (s == null || String(s).trim() === '') return '—';
      var v = String(s).trim();
      if (v.length > 1 && v.charAt(0) === '-') {
        return '(' + v.substring(1) + ')';
      }
      return v;
    },
    metricByKey(key) {
      return METRICS.filter((m) => m.key === key)[0];
    },
    // Probability-Adj. Bear IRR = Bear IRR x Bear Weight / 25%
    // i.e. the bear case scaled by how likely it is, relative to a 25% baseline weight.
    // Inputs arrive as display strings ("(6%)", "25%"); parseMetric handles the Table's
    // parenthesised-negative convention. Returns null unless BOTH inputs are present, so
    // a missing Bear Weight excludes the point rather than silently scaling it to zero.
    derivedValue(row, key) {
      if (key !== 'padjBear') return null;
      var bear = this.parseMetric(row.bearIrr);
      var weight = this.parseMetric(row.bearWeight);
      if (bear == null || weight == null) return null;
      return bear * weight / 25;
    },
    // numeric value for plotting
    metricValue(row, metric) {
      if (metric.derived) return this.derivedValue(row, metric.key);
      return this.parseMetric(row[metric.key]);
    },
    // percent to at most 1dp: -8 -> "-8%", -0.6 -> "-0.6%"
    pctStr(v) {
      if (v == null) return '—';
      return (Math.round(v * 10) / 10) + '%';
    },
    // 25% is the BASELINE bear-case probability weight. At exactly 25% the scaling factor
    // is 1, so the adjusted value IS the Bear IRR and showing the arithmetic is just noise.
    isBaselineWeight(row) {
      var w = this.parseMetric(row.bearWeight);
      return w != null && Math.abs(w - 25) < 1e-9;
    },
    // Tooltip row label. At the baseline weight the number is an unadjusted Bear IRR, so
    // it is labelled as one rather than as "Probability-Adj. Bear IRR" — calling it
    // adjusted when nothing was adjusted is the more confusing option.
    metricLabel(row, metric) {
      if (metric.derived && this.isBaselineWeight(row)) return 'Bear IRR';
      return metric.label;
    },
    // Tooltip value. Show the DERIVATION only when the weight is NOT the 25% baseline,
    // i.e. only when an adjustment actually happened:
    //   "-8% = -20% Bear IRR x (10% Bear Weight / 25%)"
    // The weight ratio is bracketed as a single scaling factor, so it reads as "the bear
    // case, scaled relative to a 25% baseline" rather than a chain of multiplications.
    // Negatives use minus signs, NOT the Table's parenthesised-negative convention:
    // parentheses are doing grouping work here, so "(8%) = (20%) Bear IRR x (10% ...)"
    // would be ambiguous as well as hard to read.
    metricDisplay(row, metric) {
      if (!metric.derived) return this.displayValue(row[metric.key]);
      var v = this.derivedValue(row, metric.key);
      if (v == null) return '—';
      if (this.isBaselineWeight(row)) return this.pctStr(v);
      return this.pctStr(v) + ' = ' + this.pctStr(this.parseMetric(row.bearIrr)) +
        ' Bear IRR x (' + this.pctStr(this.parseMetric(row.bearWeight)) + ' Bear Weight / 25%)';
    },
    axisValueFormatter(metric) {
      if (metric.kind === 'pct') return (v) => v + '%';
      if (metric.kind === 'x') return (v) => v + 'x';
      return (v) => v;
    },
    axisConfig(metric, isLog, nameGap) {
      var cfg = {
        type: isLog ? 'log' : 'value',
        name: metric.label + (isLog ? ' (log scale)' : ''),
        nameLocation: 'middle',
        nameGap: nameGap,
        nameTextStyle: { fontSize: 13, fontWeight: 'bold', color: '#304156' },
        axisLabel: { formatter: this.axisValueFormatter(metric), color: '#606266' },
        axisLine: { lineStyle: { color: '#909399' } },
        splitLine: { lineStyle: { color: '#EBEEF5' } },
      };
      if (metric.key === 'qs') {
        // QS: 自动缩放放大图表(不强制从0开始), 但上限永远不超过12
        cfg.scale = true;
        cfg.max = 12;
        cfg.minInterval = 1;
      }
      return cfg;
    },
    // 尺寸变化后重算画布和标签布局
    refit() {
      if (!this.chart) return;
      this.chart.resize();
      this.renderChart();
    },
    togglePanel() {
      this.panelOpen = !this.panelOpen;
      // canvas width animates via CSS; resize echarts after the transition
      setTimeout(() => { this.refit(); }, 280);
    },
    clearSelection() {
      this.selectedTickers = [];
      this.selectionChangedHandle();
    },
    selectionChangedHandle() {
      this.renderChart();
      this.saveStatus = 'Saving…';
      clearTimeout(this._saveTimer);
      this._saveTimer = setTimeout(this.saveSelection, 600);
    },
    saveSelection() {
      this.$http
        .post('/sys/sysstock/portfolioSelection', this.selectedTickers)
        .then(() => {
          this.saveStatus = 'Saved ✓';
          clearTimeout(this._statusTimer);
          this._statusTimer = setTimeout(() => { this.saveStatus = ''; }, 2500);
        })
        .catch(() => {
          this.saveStatus = 'Save failed';
        });
    },
    buildPoints(xM, yM, isLogX, isLogY) {
      var universe = [];
      var portfolio = [];
      var excluded = [];
      var selected = {};
      this.selectedTickers.forEach((t) => { selected[t] = true; });

      this.rows.forEach((row) => {
        var raw = (row.ticker || '').trim();
        if (!raw) return;
        var xv = this.metricValue(row, xM);
        var yv = this.metricValue(row, yM);
        if (xv == null || yv == null) {
          excluded.push({ t: this.displayTicker(raw), reason: 'no data' });
          return;
        }
        if (isLogX && xv <= 0) {
          excluded.push({ t: this.displayTicker(raw), reason: xM.label + ' ≤ 0, not plottable on log scale' });
          return;
        }
        if (isLogY && yv <= 0) {
          excluded.push({ t: this.displayTicker(raw), reason: yM.label + ' ≤ 0, not plottable on log scale' });
          return;
        }
        var pt = { name: this.displayTicker(raw), value: [xv, yv], rawTicker: raw, row: row };
        if (selected[raw]) {
          portfolio.push(pt);
        } else {
          universe.push(pt);
        }
      });
      return { universe: universe, portfolio: portfolio, excluded: excluded };
    },
    renderChart() {
      if (!this.chart) return;
      var xM = this.metricByKey(this.xMetricKey);
      var yM = this.metricByKey(this.yMetricKey);
      // R/R is shown on a log scale (flagged in the axis title); other metrics are linear
      var isLogX = xM.key === 'riskReward';
      var isLogY = yM.key === 'riskReward';
      var pts = this.buildPoints(xM, yM, isLogX, isLogY);

      // group exclusions into a compact caption
      var byReason = {};
      pts.excluded.forEach((e) => {
        (byReason[e.reason] = byReason[e.reason] || []).push(e.t);
      });
      // Explain the derived metric whenever it is on either axis. Without this the
      // reader cannot tell that most points are plain Bear IRR while a minority have
      // been rescaled — the two look identical on the chart.
      this.metricNote = (xM.derived || yM.derived)
        ? 'Probability-Adj. Bear IRR scales each Bear IRR by its Bear Weight relative to a '
          + 'baseline bear-case probability weight of 25% (Bear IRR x Bear Weight / 25%). '
          + 'Where the Bear Weight is already 25% the Bear IRR is shown unadjusted.'
        : '';

      this.excludedNote = Object.keys(byReason)
        .map((r) => 'Not shown — ' + r + ': ' + byReason[r].join(', '))
        .join('   ·   ');

      var metricDisplay = this.metricDisplay;
      var metricLabel = this.metricLabel;
      var tooltipFormatter = (p) => {
        var r = p.data.row;
        return (
          '<b>' + p.data.rawTicker + '</b><br/>' +
          metricLabel(r, xM) + ': ' + metricDisplay(r, xM) + '<br/>' +
          metricLabel(r, yM) + ': ' + metricDisplay(r, yM)
        );
      };
      var labelFormatter = (p) => p.data.name;

      var option = {
        animationDurationUpdate: 300,
        legend: {
          top: 2,
          right: 8,
          itemWidth: 12,
          itemHeight: 12,
          icon: 'circle',
          textStyle: { fontSize: 12, color: '#606266' },
          data: ['Portfolio', 'Universe'],
        },
        tooltip: {
          trigger: 'item',
          formatter: tooltipFormatter,
          backgroundColor: 'rgba(48, 65, 86, 0.92)',
          borderWidth: 0,
          textStyle: { color: '#fff', fontSize: 12 },
        },
        grid: { left: 70, right: 80, top: 36, bottom: 58 },
        xAxis: this.axisConfig(xM, isLogX, 34),
        yAxis: this.axisConfig(yM, isLogY, 52),
        series: [
          {
            name: 'Universe',
            type: 'scatter',
            data: pts.universe,
            symbolSize: 10,
            itemStyle: { color: COLOR_UNIVERSE, borderColor: COLOR_UNIVERSE_BORDER, borderWidth: 1 },
            label: {
              show: true,
              position: 'right',
              distance: 5,
              fontSize: 11,
              fontWeight: 'normal',
              color: COLOR_UNIVERSE_LABEL,
              formatter: labelFormatter,
            },
            // 引导线由layoutLabels以graphic元素绘制(仅位移标签), 禁用echarts自带labelLine
            labelLine: { show: false },
            emphasis: { scale: 1.4 },
            z: 2,
          },
          {
            name: 'Portfolio',
            type: 'scatter',
            data: pts.portfolio,
            symbolSize: 11,
            itemStyle: { color: COLOR_PORTFOLIO, borderColor: COLOR_PORTFOLIO_BORDER, borderWidth: 1 },
            label: {
              show: true,
              position: 'right',
              distance: 5,
              fontSize: 11.5,
              fontWeight: 'bold',
              color: COLOR_PORTFOLIO_LABEL,
              formatter: labelFormatter,
            },
            // 引导线由layoutLabels以graphic元素绘制(仅位移标签), 禁用echarts自带labelLine
            labelLine: { show: false },
            emphasis: { scale: 1.4 },
            z: 3,
          },
        ],
      };
      this.chart.setOption(option, { notMerge: true });
      this.layoutLabels(pts);
    },
    // 全局标签布局: 跨series解决重叠(垂直错开), 右边界翻转到左侧; 不画引导线(用户决定)
    layoutLabels(pts) {
      var chart = this.chart;
      if (!chart) return;
      if (!this._measureCtx) {
        this._measureCtx = document.createElement('canvas').getContext('2d');
      }
      var ctx = this._measureCtx;
      var GRID = { left: 70, right: 80, top: 36, bottom: 58 };
      var plotRight = chart.getWidth() - GRID.right;
      var plotTop = GRID.top;
      var plotBottom = chart.getHeight() - GRID.bottom;
      var R = 6;    // 数据点半径(symbolSize 10-11 + border)
      var GAP = 5;  // 点与标签间距
      var H = 13;   // 标签行高
      var seriesArr = [pts.universe, pts.portfolio];
      var entries = [];
      for (var si = 0; si < seriesArr.length; si++) {
        ctx.font = (si === 1 ? 'bold 11.5px ' : '11px ') + '"Helvetica Neue", Helvetica, Arial, sans-serif';
        for (var di = 0; di < seriesArr[si].length; di++) {
          var p = seriesArr[si][di];
          var px = chart.convertToPixel({ seriesIndex: si }, p.value);
          if (!px) continue;
          var w = ctx.measureText(p.name).width + 2;
          var side = px[0] + R + GAP + w > plotRight - 2 ? -1 : 1;
          entries.push({
            si: si,
            di: di,
            dotX: px[0],
            dotY: px[1],
            x: side === 1 ? px[0] + R + GAP : px[0] - R - GAP - w,
            y: px[1],
            w: w,
            side: side,
          });
        }
      }
      // 迭代松弛: 水平方向重叠的标签对在竖直方向互相推开
      for (var iter = 0; iter < 80; iter++) {
        var moved = false;
        for (var i = 0; i < entries.length; i++) {
          for (var j = i + 1; j < entries.length; j++) {
            var a = entries[i];
            var b = entries[j];
            if (a.x < b.x + b.w && b.x < a.x + a.w) {
              var dy = b.y - a.y;
              var overlap = H - Math.abs(dy);
              if (overlap > 0.5) {
                var push = overlap / 2 + 0.5;
                if (dy >= 0) {
                  a.y -= push;
                  b.y += push;
                } else {
                  a.y += push;
                  b.y -= push;
                }
                moved = true;
              }
            }
          }
        }
        if (!moved) break;
      }
      var map = {};
      entries.forEach(function (e) {
        e.y = Math.max(plotTop + H / 2, Math.min(plotBottom - H / 2, e.y));
        map[e.si + ':' + e.di] = { x: e.x, y: e.y };
      });
      var mkLayout = function (si) {
        return function (params) {
          var m = map[si + ':' + params.dataIndex];
          if (!m) return {};
          return { x: m.x, y: m.y, align: 'left', verticalAlign: 'middle' };
        };
      };
      chart.setOption({
        series: [{ labelLayout: mkLayout(0) }, { labelLayout: mkLayout(1) }],
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.mod-informationinquiry__chart {
  position: relative;
}
.chart-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  line-height: 50px;
  background: #f1f1f1;
  padding: 0 10px;
  margin-bottom: 8px;
}
.axis-pickers {
  display: flex;
  align-items: center;
}
.axis-picker-label {
  font-size: 13px;
  font-weight: bold;
  color: #304156;
  margin-right: 8px;
}
.axis-picker-label--gap {
  margin-left: 28px;
}
/* 150px truncated the longest option ("Probability-Adj. Bear IRR", 25 chars) behind the
   dropdown caret. Sized for that label plus el-select's ~30px caret gutter and padding,
   so the widest metric name shows in full when selected. */
.axis-select {
  width: 235px;
}
.updated-note {
  font-size: 12px;
  color: #909399;
}
.chart-wrap {
  position: relative;
  overflow: hidden;
}
.chart-canvas {
  width: 100%;
  height: calc(100vh - 265px);
  min-height: 460px;
  transition: width 0.25s ease;
}
.chart-canvas--shrunk {
  width: calc(100% - 250px);
}
/* collapsible portfolio selector docked on the right */
.portfolio-panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 250px;
  background: #fff;
  border-left: 1px solid #dcdfe6;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.08);
  transform: translateX(250px);
  transition: transform 0.25s ease;
  z-index: 5;
}
.portfolio-panel--open {
  transform: translateX(0);
}
.panel-toggle {
  position: absolute;
  left: -26px;
  top: 50%;
  transform: translateY(-50%);
  width: 26px;
  height: 118px;
  display: flex;
  align-items: center;
  justify-content: center;
  writing-mode: vertical-rl;
  background: #304156;
  color: #fff;
  font-size: 11px;
  letter-spacing: 2px;
  border-radius: 4px 0 0 4px;
  cursor: pointer;
  user-select: none;
}
.panel-toggle:hover {
  background: #3e5470;
}
.panel-body {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 12px 14px 4px;
}
.panel-title {
  font-size: 13px;
  font-weight: bold;
  color: #304156;
}
.panel-status {
  font-size: 11px;
  color: #67c23a;
}
.panel-status--error {
  color: #f56c6c;
}
.panel-subnote {
  padding: 0 14px 8px;
  font-size: 11px;
  line-height: 1.5;
  color: #909399;
  border-bottom: 1px solid #ebeef5;
}
.panel-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 14px;
}
.panel-item {
  display: block;
  margin: 0 0 6px 0 !important;
  ::v-deep .el-checkbox__label {
    font-size: 12px;
    color: #303133;
  }
}
.panel-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-top: 1px solid #ebeef5;
  font-size: 12px;
  color: #606266;
}
.panel-clear {
  color: #00a4fa;
  cursor: pointer;
}
.metric-note {
  margin: 8px 0 0 2px;
  font-size: 12px;
  line-height: 1.5;
  color: #909399;
}
.excluded-note {
  margin-top: 6px;
  font-size: 11px;
  color: #909399;
}
</style>
